import z from 'zod';
import { emailSchema, nameSchema, phoneSchema } from '../../validation';

/**
 * A single person attached to a Lead.
 *
 * Every field is optional so the model supports the full range of how
 * a lead enters the system:
 *
 * - **Landing form** — usually has `name`, `phone`, `email`.
 * - **On-ground sales hunter** — sometimes only has `name` + `phone`,
 *   or just a phone number.
 * - **Admin** — can attach multiple people (decision maker, accountant,
 *   ops manager, etc.) to the same lead.
 *
 * Convention: `people[0]` is the primary follow-up contact. The
 * explicit `isPrimary` flag lets admins re-prioritise without
 * reordering the array. Backend enforces "at most one `isPrimary`" at
 * write time and falls back to `people[0]` when none is set.
 */
export const leadPersonSchema = z.object({
  /** Full name of the person. */
  name: nameSchema.optional(),
  /** E.164-style phone number. */
  phone: phoneSchema.optional(),
  /** Email address. */
  email: emailSchema.optional(),
  /** Free-form job title / role (e.g. "Operations Manager"). */
  position: nameSchema.optional(),
  /**
   * Marks this entry as the primary follow-up contact. At most one
   * entry on a Lead should have `isPrimary === true`; when no entry is
   * marked, callers should fall back to `people[0]`.
   */
  isPrimary: z.boolean().optional(),
  /** Internal notes about this person ("speaks Arabic only", etc.). */
  notes: z.string().optional(),
});

export type LeadPerson = z.infer<typeof leadPersonSchema>;
